<template>
  <div class="info-feature-container" :class="spaceLevel">
    <!-- Simplified background (particles removed for performance) -->
    
    <div class="information-content">
      <div class="information-inner">
        <!-- Icon with notification pulse -->
        <div class="info-icon-container">
          <div class="info-icon">
            <i class="fas fa-info-circle"></i>
          </div>
        </div>
        
        <div class="information-body">
          <h6 class="info-title">{{ ad.title }}</h6>
          <p class="info-message">{{ ad.message }}</p>
        </div>
        
        <div v-if="ad.linkUrl" class="information-footer">
          <div class="learn-more-container">
            <span class="learn-more">เรียนรู้เพิ่มเติม</span>
            <div class="arrow-animation">
              <i class="fas fa-arrow-right"></i>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Simplified overlay (glassmorphism removed for performance) -->
    </div>
  </div>
</template>

<script>
export default {
  name: "InformationAd",
  props: {
    ad: {
      type: Object,
      required: true
    },
    spaceLevel: {
      type: String,
      default: 'normal'
    }
  },
  mounted() {
    // Emit simple loaded event for square sizing like testimonials
    this.$emit('informationLoaded', {
      aspectRatio: 1.0,
      type: 'square'
    });
  },
  // Watchers removed - using simple square sizing like testimonials
  methods: {
    openInNewTab(url) {
      if (url) {
        window.open(url, "_blank", "noreferrer");
      }
    }
    // Complex height calculations removed - using simple square sizing like testimonials
  }
};
</script>

<style scoped>
.info-feature-container {
  width: 100%;
  height: 100%;
  border-radius: 12px;
  overflow: hidden;
}

.information-content {
  width: 100%;
  min-height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: clamp(20px, 3vw, 28px);
  background: linear-gradient(135deg, #00b894 0%, #00a085 50%, #0984e3 100%);
  color: white;
  cursor: pointer;
  box-shadow: 
    0 8px 32px rgba(0, 184, 148, 0.3),
    0 4px 16px rgba(0, 0, 0, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  margin: 0 auto;
  box-sizing: border-box;
  container-type: inline-size;
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.information-inner {
  width: 100%;
  position: relative;
  z-index: 2;
}

.information-body {
  margin-bottom: 20px;
  text-align: center;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.info-title {
  font-size: clamp(24px, 4vw, 36px);
  font-weight: 800;
  margin: 0 0 clamp(12px, 1.5vw, 16px) 0;
  line-height: 1.2;
  color: white;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  letter-spacing: -0.02em;
  animation: fadeIn 0.3s ease-out;
}

.info-message {
  font-size: clamp(14px, 2.2vw, 18px);
  line-height: 1.6;
  margin: 0;
  opacity: 0.95;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
  animation: fadeIn 0.4s ease-out 0.1s both;
}

.information-footer {
  text-align: center;
  padding-top: 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.3);
  margin-top: 16px;
  animation: fadeIn 0.4s ease-out 0.2s both;
}

.learn-more-container {
  display: flex;
  align-items: center;
  gap: 8px;
  transition: transform 0.2s ease;
}

.learn-more {
  font-size: clamp(12px, 1.8vw, 16px);
  font-weight: 700;
  opacity: 0.95;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
}

.arrow-animation {
  transition: transform 0.2s ease;
}

.arrow-animation i {
  font-size: clamp(10px, 1.5vw, 14px);
  /* Arrow pulse animation removed for performance */
}

/* Simplified hover effects for better performance */
.information-content:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(0, 184, 148, 0.3);
}

.information-content:hover .learn-more-container {
  transform: translateX(2px);
}

/* Particle and pulse animations removed for performance */

/* Space level responsive adjustments */
.info-feature-container.minimal .information-content {
  padding: 16px;
}

.info-feature-container.minimal .information-content {
  padding: 16px;
}

.info-feature-container.minimal .info-title {
  font-size: 18px;
  margin-bottom: 8px;
}

.info-feature-container.minimal .info-message {
  font-size: 11px;
}

.info-feature-container.minimal .learn-more {
  font-size: 10px;
}

.info-feature-container.limited .information-content {
  padding: 22px;
}

.info-feature-container.limited .info-title {
  font-size: 20px;
}

.info-feature-container.limited .info-message {
  font-size: 12px;
}

.info-feature-container.limited .learn-more {
  font-size: 11px;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .information-content {
    padding: 20px;
  }
  
  .info-title {
    font-size: 18px;
  }
  
  .info-message {
    font-size: 12px;
  }
  
  .learn-more {
    font-size: 10px;
  }
}

/* Background particles removed for performance */

/* Icon with pulse effect */
.info-icon-container {
  position: relative;
  display: flex;
  justify-content: center;
  margin-bottom: 16px;
  animation: fadeIn 0.3s ease-out;
}

.info-icon {
  width: 48px;
  height: 48px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  color: white;
  position: relative;
  z-index: 2;
  border: 1px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
}

/* Pulse ring removed for performance */

/* Glassmorphism overlay removed for performance */

/* Simplified keyframe animations for better performance */
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* Respect reduced motion preferences */
@media (prefers-reduced-motion: reduce) {
  .information-content,
  .info-title,
  .info-message,
  .information-footer,
  .info-icon-container {
    animation: none;
  }
  
  .information-content:hover {
    transform: none;
  }
}

/* Emergency fallback for very small screens */
@media (max-height: 500px) {
  .information-content {
    padding: 15px;
  }
  
  .information-body {
    margin-bottom: 15px;
  }
  
  .info-title {
    font-size: 16px;
    margin-bottom: 8px;
  }
  
  .info-message {
    font-size: 11px;
  }
  
  .learn-more {
    font-size: 9px;
  }
  
  .info-icon-container {
    margin-bottom: 12px;
  }
  
  .info-icon {
    width: 36px;
    height: 36px;
    font-size: 18px;
  }
}
</style>